import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { MessageSquare, Eye, EyeOff, Mail, Lock, AlertCircle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

// Google "G" brand mark (lucide has no brand icons)
const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
    <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
    <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 6.1 29.6 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
    <path fill="#4CAF50" d="M24 44c5.5 0 10.5-2.1 14.3-5.6l-6.6-5.6C29.6 34.6 26.9 36 24 36c-5.2 0-9.6-3.3-11.2-7.9l-6.6 5.1C9.6 39.6 16.2 44 24 44z" />
    <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4 5.4l6.6 5.6C41.4 36.3 44 30.7 44 24c0-1.3-.1-2.4-.4-3.5z" />
  </svg>
);

const LoginPage = ({ darkMode, toggleDarkMode }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const navigate = useNavigate();
  const { signIn, signInWithGoogle } = useAuth();

  const handleGoogle = async () => {
    setError('');
    setGoogleLoading(true);
    try {
      await signInWithGoogle();
      // Browser redirects to Google; on return, AuthContext routes to /dashboard.
    } catch (err) {
      setError(err.message);
      setGoogleLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError('');

    try {
      await signIn(email, password);
      navigate('/dashboard');
    } catch (error) {
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-canvas' : 'bg-gray-50'} flex items-center justify-center px-4 sm:px-6 lg:px-8`}>
      
      <div className="relative max-w-md w-full space-y-4">
        {/* Header */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center"
        >
          <div className="inline-flex items-center space-x-2 mb-4">
            <div className={`w-10 h-10 bg-gradient-to-r from-accent to-accent-hover rounded flex items-center justify-center`}>
              <MessageSquare className="w-6 h-6 text-primary" />
            </div>
            <span className={`text-2xl font-bold bg-gradient-to-r from-accent to-accent-hover bg-clip-text text-transparent`}>
              ICEDMS
            </span>
          </div>

          <h1 className={`text-2xl font-bold ${darkMode ? 'text-primary' : 'text-gray-900'} mb-1`}>
            Welcome back
          </h1>
          <p className={`text-sm ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>
            Sign in to your account to continue
          </p>
        </motion.div>

        {/* Login Form */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className={`${darkMode ? 'bg-surface border-border' : 'bg-white border-gray-200'} rounded p-6 shadow-xl border`}
        >
          {error && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className={`mb-6 p-4 rounded ${darkMode ? 'bg-red-500/20 border-red-500/50' : 'bg-red-50 border-red-200'} border flex items-center space-x-3`}
            >
              <AlertCircle className={`w-5 h-5 ${darkMode ? 'text-red-400' : 'text-red-600'} flex-shrink-0`} />
              <p className={`text-sm ${darkMode ? 'text-red-300' : 'text-red-700'}`}>{error}</p>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email Input */}
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-primary' : 'text-gray-700'} mb-1.5`}>
                Email Address
              </label>
              <div className="relative">
                <Mail className={`w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-secondary' : 'text-gray-400'}`} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className={`pl-10 pr-4 py-2.5 w-full border rounded focus:ring-2 focus:border-transparent transition-colors ${
                    darkMode 
                      ? 'bg-elevated border-border text-primary placeholder-secondary focus:ring-accent' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-accent'
                  }`}
                  placeholder="Enter your email"
                  autoComplete="email"
                />
              </div>
            </div>

            {/* Password Input */}
            <div>
              <label className={`block text-sm font-medium ${darkMode ? 'text-primary' : 'text-gray-700'} mb-1.5`}>
                Password
              </label>
              <div className="relative">
                <Lock className={`w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-secondary' : 'text-gray-400'}`} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className={`pl-10 pr-12 py-2.5 w-full border rounded focus:ring-2 focus:border-transparent transition-colors ${
                    darkMode 
                      ? 'bg-elevated border-border text-primary placeholder-secondary focus:ring-accent' 
                      : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-accent'
                  }`}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${darkMode ? 'text-secondary hover:text-primary' : 'text-gray-400 hover:text-gray-600'}`}
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Forgot Password */}
            <div className="flex justify-end">
              <Link 
                to="/forgot-password" 
                className={`text-sm ${darkMode ? 'text-accent hover:text-accent-hover' : 'text-accent hover:text-accent-pressed'} hover:underline`}
              >
                Forgot your password?
              </Link>
            </div>

            {/* Submit Button */}
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              type="submit"
              disabled={loading}
              className={`w-full bg-gradient-to-r from-accent to-accent-hover ${darkMode ? 'shadow-lg shadow-accent/25' : ''} text-white py-3 rounded font-semibold hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {loading ? (
                <div className="flex items-center justify-center space-x-2">
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Signing in...</span>
                </div>
              ) : (
                'Sign In'
              )}
            </motion.button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-3 my-4">
            <div className={`flex-1 h-px ${darkMode ? 'bg-border' : 'bg-gray-200'}`} />
            <span className={`text-xs uppercase tracking-wide ${darkMode ? 'text-secondary' : 'text-gray-400'}`}>or</span>
            <div className={`flex-1 h-px ${darkMode ? 'bg-border' : 'bg-gray-200'}`} />
          </div>

          {/* Google Sign-In */}
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            type="button"
            onClick={handleGoogle}
            disabled={googleLoading}
            className={`w-full flex items-center justify-center gap-3 py-3 rounded font-semibold border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              darkMode
                ? 'bg-elevated border-border text-primary hover:bg-surface'
                : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
            }`}
          >
            {googleLoading ? (
              <div className="w-5 h-5 border-2 border-current/30 border-t-current rounded-full animate-spin" />
            ) : (
              <GoogleIcon />
            )}
            <span>Continue with Google</span>
          </motion.button>

          {/* Sign Up Link */}
          <div className="mt-4 text-center">
            <p className={`text-sm ${darkMode ? 'text-secondary' : 'text-gray-600'}`}>
              Don't have an account?{' '}
              <Link 
                to="/signup" 
                className={`font-semibold ${darkMode ? 'text-accent hover:text-accent-hover' : 'text-accent hover:text-accent-pressed'} hover:underline`}
              >
                Sign up
              </Link>
            </p>
          </div>
        </motion.div>

        {/* Dark Mode Toggle */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="text-center"
        >
          <button 
            onClick={toggleDarkMode}
            className={`p-2 rounded transition-colors ${
              darkMode 
                ? 'text-secondary hover:text-primary hover:bg-elevated' 
                : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
            }`}
            title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          >
            {darkMode ? <Eye className="w-5 h-5" /> : <EyeOff className="w-5 h-5" />}
          </button>
        </motion.div>
      </div>
    </div>
  );
};

export default LoginPage;